/**
 * usePickerFilterGraph (ORCH-400 §4.4, §4.6)
 *
 * Purpose: The filter graph of a model: for every list Field, which other fields (on the
 *          model or an ancestor) narrow its picker, as the server resolved them from the
 *          related-field definitions and each Field's filter_config.
 * Role in system: Read by <PickerFilters> to build each picker's `filter_by`, and by the
 *          Field modal's "Filtered by" section. One cache entry per model and tenant,
 *          with the view configuration's stale time, so a form doesn't refetch it per picker.
 * Dependencies: @tanstack/react-query, metadataService.getPickerFilters
 */

import { useQuery } from '@tanstack/react-query';
import { metadataService, type PickerFilterGraph } from '../services/metadataService';

/**
 * Bumped when a Field's filter_config is saved in the Metadata Manager, so the next
 * form to mount reads the new graph instead of the one cached up to five minutes ago.
 * A counter in the key, rather than a QueryClient call, because the Field modal and its
 * manager render outside any query hook.
 */
let generation = 0;

export const invalidatePickerFilterGraph = (): void => {
  generation += 1;
};

export const pickerFilterGraphQueryKey = (
  modelId: string,
  tenantId: string
): readonly unknown[] => ['metadata', 'picker-filters', modelId, tenantId, generation];

export const usePickerFilterGraph = (
  modelId: string | undefined,
  tenantId: string | undefined,
  options?: { enabled?: boolean }
): { graph: PickerFilterGraph | undefined; isLoading: boolean; error: unknown } => {
  const query = useQuery({
    queryKey: pickerFilterGraphQueryKey(modelId ?? '', tenantId ?? ''),
    queryFn: () => metadataService.getPickerFilters(modelId as string),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false && !!modelId && !!tenantId,
  });
  return { graph: query.data, isLoading: query.isLoading, error: query.error };
};
